import { useOrganization } from '../context/OrganizationContext';

/**
 * useOrganizationAccess Hook
 *
 * Provides easy access to organization subscription status and permissions.
 * Use this hook in components to determine if users can create/edit/delete.
 *
 * Returns:
 * - hasWriteAccess: boolean - true if subscription is active (can modify data)
 * - isReadOnly: boolean - true if subscription is inactive (view-only)
 * - subscriptionStatus: string - current subscription status
 * - canAccessFeature: function - check if a specific feature is available
 *
 * Usage:
 * const { hasWriteAccess, isReadOnly } = useOrganizationAccess();
 *
 * <Button disabled={!hasWriteAccess} onClick={handleCreate}>
 *   Add Drone
 * </Button>
 */
export function useOrganizationAccess() {
  const { subscriptionStatus, currentOrganization } = useOrganization();

  // Active and trialing subscriptions have full write access
  const hasWriteAccess =
    subscriptionStatus === 'active' ||
    subscriptionStatus === 'trialing';

  const isReadOnly = !hasWriteAccess;

  /**
   * Check if a specific feature is available based on subscription tier
   * @param {string} feature - Feature name to check
   * @returns {boolean}
   */
  const canAccessFeature = (feature) => {
    if (!currentOrganization?.subscription) {
      return false;
    }

    const { planName } = currentOrganization.subscription;

    // Feature availability by plan
    const featureMatrix = {
      'basic_analytics': ['Team Plan', 'Business Plan', 'Enterprise Plan'],
      'advanced_analytics': ['Business Plan', 'Enterprise Plan'],
      'api_access': ['Enterprise Plan'],
      'white_label': ['Enterprise Plan'],
      'custom_integrations': ['Enterprise Plan'],
      'priority_support': ['Business Plan', 'Enterprise Plan'],
      'multi_location': ['Enterprise Plan']
    };

    const allowedPlans = featureMatrix[feature] || [];
    return allowedPlans.includes(planName);
  };

  /**
   * Get a user-friendly message about why an action is disabled
   * @returns {string}
   */
  const getDisabledMessage = () => {
    if (subscriptionStatus === 'past_due') {
      return 'Payment past due. Please update your payment method.';
    }
    if (subscriptionStatus === 'canceled') {
      return 'Subscription canceled. Reactivate to enable this action.';
    }
    if (subscriptionStatus === 'incomplete') {
      return 'Subscription incomplete. Complete payment to enable this action.';
    }
    return 'Subscription inactive. Reactivate to enable this action.';
  };

  return {
    hasWriteAccess,
    isReadOnly,
    subscriptionStatus,
    canAccessFeature,
    getDisabledMessage,
    currentOrganization
  };
}

export default useOrganizationAccess;
